import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CollectionsBookmarkRoundedIcon from "@mui/icons-material/CollectionsBookmarkRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import LocalLibraryRoundedIcon from "@mui/icons-material/LocalLibraryRounded";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import PsychologyRoundedIcon from "@mui/icons-material/PsychologyRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import BusinessContainer from "../../common/BusinessContainer";
import styles from "../css/LandingScreen.module.css";

interface LandingFeature {
  icon: JSX.Element;
  color: string;
  titleKey: string;
  descriptionKey: string;
}

const features: LandingFeature[] = [
  {
    icon: <CollectionsBookmarkRoundedIcon fontSize="inherit" />,
    color: "var(--icon-purple-color)",
    titleKey: "LandingFeatureVocabularyTitle",
    descriptionKey: "LandingFeatureVocabularyDescription",
  },
  {
    icon: <AutoAwesomeRoundedIcon fontSize="inherit" />,
    color: "var(--icon-blue-color)",
    titleKey: "LandingFeatureAddTitle",
    descriptionKey: "LandingFeatureAddDescription",
  },
  {
    icon: <LocalLibraryRoundedIcon fontSize="inherit" />,
    color: "var(--icon-mint-color)",
    titleKey: "LandingFeatureLibraryTitle",
    descriptionKey: "LandingFeatureLibraryDescription",
  },
  {
    icon: <RecordVoiceOverRoundedIcon fontSize="inherit" />,
    color: "var(--icon-pink-color)",
    titleKey: "LandingFeatureVoiceTitle",
    descriptionKey: "LandingFeatureVoiceDescription",
  },
  {
    icon: <PsychologyRoundedIcon fontSize="inherit" />,
    color: "var(--icon-green-color)",
    titleKey: "LandingFeatureLearningTitle",
    descriptionKey: "LandingFeatureLearningDescription",
  },
  {
    icon: <NotificationsActiveRoundedIcon fontSize="inherit" />,
    color: "var(--icon-orange-color)",
    titleKey: "LandingFeatureNotificationTitle",
    descriptionKey: "LandingFeatureNotificationDescription",
  },
  {
    icon: <GroupsRoundedIcon fontSize="inherit" />,
    color: "var(--icon-grayish-blue-color)",
    titleKey: "LandingFeatureStudyGroupTitle",
    descriptionKey: "LandingFeatureStudyGroupDescription",
  },
];

const highlights = [
  {
    titleKey: "LandingHighlightLanguagesTitle",
    descriptionKey: "LandingHighlightLanguagesDescription",
  },
  {
    titleKey: "LandingHighlightAiTitle",
    descriptionKey: "LandingHighlightAiDescription",
  },
  {
    titleKey: "LandingHighlightImportTitle",
    descriptionKey: "LandingHighlightImportDescription",
  },
];

function LandingScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const languages = t("LandingLanguageList", {
    returnObjects: true,
  }) as string[];

  function handleClickLoginButton() {
    navigate("/login");
  }

  return (
    <div className={styles.landingScreen}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <img
            className={styles.headerLogo}
            src="/imgOnevocaLogoBlue.png"
            alt="Onevoca"
          />
          <button
            className={styles.secondaryButton}
            onClick={() => handleClickLoginButton()}
          >
            {t("SignIn")}
          </button>
        </div>
      </header>

      <main>
        <section className={styles.hero}>
          <div className={styles.heroGlow} />
          <div className={styles.heroInner}>
            <p className={styles.heroBadge}>{t("LandingHeroBadge")}</p>
            <h1 className={styles.heroTitle}>{t("LandingHeroTitle")}</h1>
            <p className={styles.heroDescription}>
              {t("LandingHeroDescription")}
            </p>
            <button
              className={styles.primaryButton}
              onClick={() => handleClickLoginButton()}
            >
              {t("LandingStartAction")}
            </button>
            <p className={styles.heroNote}>{t("LandingHeroNote")}</p>
          </div>
        </section>

        <section className={styles.highlights}>
          {highlights.map((highlight) => (
            <div className={styles.highlightCard} key={highlight.titleKey}>
              <p className={styles.highlightTitle}>{t(highlight.titleKey)}</p>
              <p className={styles.highlightDescription}>
                {t(highlight.descriptionKey)}
              </p>
            </div>
          ))}
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("LandingFeaturesTitle")}</h2>
          <p className={styles.sectionDescription}>
            {t("LandingFeaturesDescription")}
          </p>
          <div className={styles.featureGrid}>
            {features.map((feature) => (
              <div className={styles.featureCard} key={feature.titleKey}>
                <div
                  className={styles.featureIcon}
                  style={{ color: feature.color }}
                >
                  {feature.icon}
                </div>
                <p className={styles.featureTitle}>{t(feature.titleKey)}</p>
                <p className={styles.featureDescription}>
                  {t(feature.descriptionKey)}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className={styles.section}>
          <h2 className={styles.sectionTitle}>{t("LandingLanguagesTitle")}</h2>
          <p className={styles.sectionDescription}>
            {t("LandingLanguagesDescription")}
          </p>
          <ul className={styles.languageList}>
            {languages.map((language) => (
              <li className={styles.languageChip} key={language}>
                {language}
              </li>
            ))}
          </ul>
        </section>

        <section className={styles.section}>
          <div className={styles.ctaCard}>
            <h2 className={styles.ctaTitle}>{t("LandingCtaTitle")}</h2>
            <p className={styles.ctaDescription}>{t("LandingCtaDescription")}</p>
            <button
              className={styles.ctaButton}
              onClick={() => handleClickLoginButton()}
            >
              {t("LandingStartAction")}
            </button>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <BusinessContainer />
        <p className={styles.footerCopyright}>{t("LandingCopyright")}</p>
      </footer>
    </div>
  );
}

export default LandingScreen;
